import React, {
  Children,
  useRef,
  useState,
  useCallback,
  useImperativeHandle,
  useEffect,
} from 'react';
import TwoVerticalLineIcon from '@icons/sankey/two-vertical-line.svg';
import ArrowRightOutlinedIcon from '@icons/sankey/arrow-right-outlined.svg';
import {
  DragBar,
  DragIcon,
  StyledLeftPanel,
  ResizableContainer,
  StyledRightPanel,
  CollapseIcon,
  Overlay,
  LeftOverlay,
} from './styled';
import { useWorkflowStore } from '@src/pages/workflow/store';

const INIT_LEFT_WIDTH = 300;
const COLLAPSE_WIDTH = 30;

interface IResizablePanelProps {
  initLeftWidth?: number;
  maxLeftWidth?: number;
  children: React.ReactNode;
}

export interface IResizablePanelRef {
  foldPanel: () => void;
}

const ResizablePanel = ({ initLeftWidth, maxLeftWidth, children }: IResizablePanelProps, ref) => {
  const [leftWidth, setLeftWidth] = useState(initLeftWidth || INIT_LEFT_WIDTH); // Initial width of the left panel
  const containerRef = useRef<HTMLDivElement>(null);
  const isDraggingRef = useRef(false);
  const [isDragging, setIsDragging] = useState(false);
  const { isPanelOpen, setIsPanelOpen } = useWorkflowStore();

  const handleMouseDown = () => {
    isDraggingRef.current = true;
    setIsDragging(true);
    setIsPanelOpen(true);
  };

  const foldPanel = useCallback(() => {
    setLeftWidth(COLLAPSE_WIDTH);
  }, []);

  const unfoldPanel = useCallback(() => {
    setLeftWidth(initLeftWidth || INIT_LEFT_WIDTH);
  }, [initLeftWidth]);

  const handleCollapse = useCallback(() => {
    setIsPanelOpen(!isPanelOpen);
  }, [foldPanel, isPanelOpen, unfoldPanel]);

  useEffect(() => {
    if (isPanelOpen === false) {
      foldPanel();
    } else {
      unfoldPanel();
    }
  }, [isPanelOpen, foldPanel, unfoldPanel]);

  const handleMouseMove = useCallback(
    (e) => {
      if (!isDraggingRef.current) return;

      const containerOffsetLeft = containerRef.current?.offsetLeft || 0;
      const newLeftWidth = e.clientX - containerOffsetLeft;

      let width = newLeftWidth;
      if (maxLeftWidth && newLeftWidth > maxLeftWidth) {
        width = maxLeftWidth;
      }

      setLeftWidth(width);
    },
    [maxLeftWidth],
  );

  const handleMouseUp = useCallback(() => {
    if (isDraggingRef.current) {
      isDraggingRef.current = false;
      setIsDragging(false);
    }
  }, []);

  React.useEffect(() => {
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [handleMouseMove, handleMouseUp]);

  useImperativeHandle(
    ref,
    () => {
      return {
        foldPanel,
      };
    },
    [foldPanel],
  );

  const [leftChild, rightChild] = Children.toArray(children);

  return (
    <ResizableContainer ref={containerRef}>
      <StyledLeftPanel $isDragging={isDragging} style={{ width: leftWidth }}>
        {leftChild}
        {!isPanelOpen && <LeftOverlay />}
      </StyledLeftPanel>
      <DragBar>
        {isPanelOpen && <DragIcon component={TwoVerticalLineIcon} onMouseDown={handleMouseDown} />}
        {!isPanelOpen && (
          <CollapseIcon
            rotate={isPanelOpen ? 180 : 0}
            component={ArrowRightOutlinedIcon}
            onClick={handleCollapse}
            onMouseDown={(e) => {
              e.stopPropagation();
              e.preventDefault();
            }}
          />
        )}
      </DragBar>
      <StyledRightPanel>{rightChild}</StyledRightPanel>
      {isDragging && <Overlay />}
    </ResizableContainer>
  );
};

export default React.forwardRef<IResizablePanelRef, IResizablePanelProps>(ResizablePanel);
